<!--
 * @Description: 聊天会员用户信息卡片
 * 展示用户头像、昵称、会员类型和到期日期
 * 支持月度会员和年度会员两种样式
-->
<template>
    <div
        v-if="hasChatMember"
        class="chat-member-info-card"
        :class="isYearlyChatMember ? 'yearly-member' : 'monthly-member'"
    >
        <div class="flex items-center gap-3">
            <!-- 用户头像 -->
            <img
                :src="userAvatar"
                :alt="userName"
                class="w-10 h-10 rounded-full object-cover border"
                :class="
                    isYearlyChatMember
                        ? 'border-[#5C3D1E]/30'
                        : 'border-white/20'
                "
            />
            <!-- 用户信息 -->
            <div class="flex flex-col">
                <span
                    class="font-medium text-base"
                    :class="
                        isYearlyChatMember ? 'text-[#5C3D1E]' : 'text-white'
                    "
                >
                    {{ userName }}
                </span>
                <span
                    class="text-sm"
                    :class="
                        isYearlyChatMember ? 'text-[#8B6914]' : 'text-white/60'
                    "
                >
                    {{ memberTypeText }} ({{ expireDate }}
                    {{ t('vip.expireDate') }})
                </span>
            </div>
        </div>
        <!-- 已激活标签 -->
        <div
            class="active-badge"
            :class="isYearlyChatMember ? 'yearly-badge' : 'monthly-badge'"
        >
            {{ t('vip.activated') }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useUserStore } from '~/stores/user'
import { useI18n } from '~/composables/useI18n'

const { t } = useI18n()
const userStore = useUserStore()

// 聊天会员信息
const chatSubInfo = computed(() => userStore.accountSummary?.chat_sub_info)

// 是否已开通聊天会员
const hasChatMember = computed(() => !!chatSubInfo.value)

// 是否年度会员
const isYearlyChatMember = computed(
    () => chatSubInfo.value?.period_type === 'YEARLY'
)

// 是否月度会员
const isMonthlyChatMember = computed(
    () => chatSubInfo.value?.period_type === 'MONTHLY'
)

// 用户名
const userName = computed(() => userStore.userInfo?.nick_name || '')

// 用户头像
const userAvatar = computed(
    () =>
        userStore.userInfo?.avatar ||
        'https://static.weryai.com/growth/image/weryai_e44e2e0332eab2a003546bb2ac831791a1a42d90.png'
)

// 会员到期日期
const expireDate = computed(() => {
    if (!chatSubInfo.value?.next_billing_date) return ''
    return chatSubInfo.value.next_billing_date
})

// 会员类型文案
const memberTypeText = computed(() => {
    if (isYearlyChatMember.value) {
        return t('vip.yearlyMemberActive')
    } else if (isMonthlyChatMember.value) {
        return t('vip.monthlyMemberActive')
    }
    return ''
})
</script>

<style lang="less" scoped>
/* 聊天会员用户信息卡片 */
.chat-member-info-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-radius: 16px;
    width: 100%;
}

/* 月度会员样式 */
.chat-member-info-card.monthly-member {
    border: 1px solid rgba(44, 44, 44, 0.76);
    background: rgba(10, 10, 10, 0.6);
}

/* 年度会员样式 */
.chat-member-info-card.yearly-member {
    background: linear-gradient(91deg, #fdf5ea 0.95%, #c6a07c 88.54%);
}

/* 已激活标签 */
.active-badge {
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    flex-shrink: 0;
}

/* 月度会员激活标签 */
.active-badge.monthly-badge {
    background: rgba(255, 214, 50, 0.1);
    border: 1px solid #ffd632;
    color: #ffd632;
}

/* 年度会员激活标签 */
.active-badge.yearly-badge {
    background: rgba(255, 255, 255, 0.3);
    border: 1px solid rgba(92, 61, 30, 0.5);
    color: #5c3d1e;
}
</style>
